import { BOOKING_SEASON_CLOSED_CODE, type PublicBookingSeasonStatus } from "@/app/appointments/booking-season/bookingSeasonApi";
import { BOOKING_SEASON_CLOSED_TERMINAL } from "./publicBookingFlowTypes";
import type { TerminalState } from "./publicBookingFlowSlice";

/**
 * What a public booking page shows for the booking season, decided before anything bookable renders.
 *
 * - `loading`: the status has not arrived yet. Render a placeholder, never the ZIP step — showing it
 *   and then swapping it for the closed notice would invite somebody to start typing.
 * - `closed`: render the closed notice with `message` in place of anything that starts a booking.
 * - `open`: carry on as normal.
 * - `unavailable`: the status could not be read. Offer a retry, never the ZIP step — the season may
 *   well be closed — and never the closed notice either, which would tell the public something that
 *   may not be true.
 */
export type BookingSeasonGate =
  | { state: "loading" }
  | { state: "closed"; message: string | null }
  | { state: "open" }
  | { state: "unavailable" };

/** The parts of the useGetPublicBookingSeasonQuery result the decision reads. */
export interface PublicBookingSeasonQueryResult {
  data?: PublicBookingSeasonStatus;
  isLoading: boolean;
  isError: boolean;
}

export function bookingSeasonGate({ data, isLoading, isError }: PublicBookingSeasonQueryResult): BookingSeasonGate {
  // A failed status read is neither answer. Treating it as open would show the ZIP step while the
  // season may be closed (the server would still refuse with a 409, but the page would have offered a
  // booking it cannot take); treating it as closed would turn a blip on one GET into "we are not
  // booking" for everyone. So the page asks to try again.
  if (isError) {
    return { state: "unavailable" };
  }
  if (isLoading || !data) {
    return { state: "loading" };
  }
  if (data.status === "Closed") {
    return { state: "closed", message: data.message };
  }
  return { state: "open" };
}

/**
 * The booking_season_closed terminal for a refusal from reserve, create or the upcoming-household
 * check, or null for any other failure. The refusal is a 409 whose body is
 * `{ error, code: "bookingSeasonClosed" }`, `error` being the season's website message.
 *
 * Matched on `code` and not on the status alone: create also answers 409 for a household that is
 * already booked, and that one has a terminal of its own.
 */
export function bookingSeasonClosedTerminal(err: unknown): TerminalState | null {
  if (!err || typeof err !== "object" || (err as { status?: unknown }).status !== 409) {
    return null;
  }
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== "object" || (data as { code?: unknown }).code !== BOOKING_SEASON_CLOSED_CODE) {
    return null;
  }
  const error = (data as { error?: unknown }).error;
  return { type: BOOKING_SEASON_CLOSED_TERMINAL, detail: { message: typeof error === "string" ? error : null } };
}
